/**
 * 代理专属开户链公开 API（PAGE-MCH-USER-008 / API-ONB-PUB-001~003）。
 * 无 JWT：本模块刻意不引用门户 client / 会话存储。
 */

function resolveMerchantApiBase(): string {
  const prod = Boolean((import.meta as { env?: { PROD?: boolean } }).env?.PROD);
  return (prod
    ? 'https://allinks-api.swapsle.com/merchant/api/v1'
    : '/merchant/api/v1'
  ).replace(/\/$/, '');
}

function merchantApiUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${resolveMerchantApiBase()}${p}`;
}

export const INVITE_GONE_COPY = '链接已失效';
const INVITE_GONE_CODE = '3211';

type PublicApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; code?: string };

/** 预检产品：仅 id/名称/卡种，不含费率。 */
export type PublicProductOption = {
  productId: string;
  productName: string;
  cardType: string;
};

type PublicAgentInvitePreview = {
  agentDisplayName: string;
  products: PublicProductOption[];
};

export type PublicUboItem = {
  uboId?: string;
  uboName?: string;
  uboNationality?: string;
  uboIdType?: string;
  uboIdNo?: string;
  uboIdFiles?: string[];
  uboBirthday?: string;
  uboAddress?: string;
  uboAddressCountry?: string;
  /** 持股比例字符串，禁 number/double */
  ownershipPercentage?: string;
  controlType?: string;
  isPoliticallyExposedPerson?: boolean;
  pepDetails?: string;
};

type PublicSubmitBody = {
  legalName: string;
  loginEmail: string;
  registrationNo: string;
  licenseFileId: string;
  legalPersonName: string;
  legalPersonIdType: string;
  legalPersonIdNo: string;
  legalPersonIdFiles: string[];
  intendedProducts: string[];
  uboList: PublicUboItem[];
};

export type PublicSubmitResult = {
  applicationId?: string;
  status?: string;
  type?: string;
  source?: string;
  legalName?: string;
  resubmitFields?: string[];
};

type PublicFileUploadResult = {
  fileId: string;
  contentType?: string;
  sizeBytes?: number;
  originalFilename?: string | null;
  scanStatus?: string | null;
};

type Envelope<T> = {
  code?: string | number;
  message?: string;
  data?: T;
};

function isOkCode(code: string | number | undefined): boolean {
  return code === 0 || code === '0' || code === undefined;
}

function newIdempotencyKey(): string {
  return crypto.randomUUID?.() ?? `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * 从 location.search 取 invite。无 token / 空白 → null。
 * 必须走 URLSearchParams.get('invite')，不得另造 query 名。
 */
export function inviteTokenFromSearch(search: string): string | null {
  const query = search.startsWith('?') ? search.slice(1) : search;
  const raw = new URLSearchParams(query).get('invite');
  if (raw == null) {
    return null;
  }
  const token = raw.trim();
  return token.length > 0 ? token : null;
}

/** 无 token 或业务码 3211 → 链接已失效。 */
export function isInviteGone(token: string | null, code?: string | number | null): boolean {
  if (!token) {
    return true;
  }
  return String(code ?? '') === INVITE_GONE_CODE;
}

/** 无 invite / 3211 时页面应渲染的失效文案；其它错误返回 null。 */
export function inviteGoneCopy(token: string | null, code?: string | number | null): string | null {
  return isInviteGone(token, code) ? INVITE_GONE_COPY : null;
}

function invitePath(token: string, suffix = ''): string {
  return `/public/agent-invites/${encodeURIComponent(token)}${suffix}`;
}

async function parsePublic<T>(res: Response): Promise<PublicApiResult<T>> {
  let body: Envelope<T> | T | null = null;
  try {
    body = (await res.json()) as Envelope<T> | T;
  } catch {
    return {
      ok: false,
      error: res.ok ? 'invalid_json' : `HTTP ${res.status}`,
    };
  }
  const env =
    body && typeof body === 'object' && 'code' in (body as object)
      ? (body as Envelope<T>)
      : null;
  const code = env?.code !== undefined ? String(env.code) : undefined;
  if (!res.ok) {
    return {
      ok: false,
      code,
      error: env?.message || code || `HTTP ${res.status}`,
    };
  }
  if (env && !isOkCode(env.code)) {
    return {
      ok: false,
      code,
      error: env.message || code || 'request_failed',
    };
  }
  return { ok: true, data: (env ? env.data : body) as T };
}

function toPublicProducts(raw: unknown): PublicProductOption[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const products: PublicProductOption[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const row = item as Record<string, unknown>;
    const productId = row.productId == null ? '' : String(row.productId);
    if (!productId) {
      continue;
    }
    // 只投影 id/名称/卡种，丢弃费率字段
    products.push({
      productId,
      productName: row.productName == null ? '' : String(row.productName),
      cardType: row.cardType == null ? '' : String(row.cardType),
    });
  }
  return products;
}

function normalizePreview(raw: PublicAgentInvitePreview | null | undefined): PublicAgentInvitePreview {
  return {
    agentDisplayName: raw?.agentDisplayName ? String(raw.agentDisplayName) : '',
    products: toPublicProducts(raw?.products),
  };
}

/** GET 预检。匿名、不带鉴权头。 */
export async function previewPublicAgentInvite(
  token: string,
): Promise<PublicApiResult<PublicAgentInvitePreview>> {
  try {
    const res = await fetch(merchantApiUrl(invitePath(token)), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
    });
    const parsed = await parsePublic<PublicAgentInvitePreview>(res);
    if (!parsed.ok) {
      return parsed;
    }
    return { ok: true, data: normalizePreview(parsed.data) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'network_error' };
  }
}

/** POST 槽位上传（multipart file+slot）。匿名；不手工写 Content-Type。 */
export async function uploadPublicInviteFile(
  token: string,
  file: File,
  slot: 'IDENTITY' | 'FUND',
): Promise<PublicApiResult<PublicFileUploadResult>> {
  const form = new FormData();
  form.append('file', file);
  form.append('slot', slot);
  try {
    const res = await fetch(merchantApiUrl(invitePath(token, '/files')), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      body: form,
    });
    return parsePublic<PublicFileUploadResult>(res);
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'network_error' };
  }
}

/** POST 齐件提交。匿名；必带 Idempotency-Key。 */
export async function submitPublicAgentInvite(
  token: string,
  body: PublicSubmitBody,
  idempotencyKey?: string,
): Promise<PublicApiResult<PublicSubmitResult>> {
  try {
    const res = await fetch(merchantApiUrl(invitePath(token, '/applications')), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey ?? newIdempotencyKey(),
      },
      credentials: 'omit',
      body: JSON.stringify(body),
    });
    return parsePublic<PublicSubmitResult>(res);
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'network_error' };
  }
}
